import type { FieldKind, NormRect, RecipientRole } from './api'
import { specFor } from './fields'
import { signsDocument } from './newEnvelopeDraft'

/**
 * Finding where a document asks to be filled in.
 *
 * Everything here reads the PDF's own text layer, in the sender's browser, and
 * proposes. Nothing it finds becomes a box until the sender accepts it: what a
 * signer is asked for is the sender's decision, and a box that was guessed and
 * sent unseen is one nobody chose. A scanned document has no text layer, so it
 * gets no suggestions rather than wrong ones.
 */

/** One piece of text as pdf.js positions it. `y` is the baseline. */
export interface TextRun {
  str: string
  x: number
  y: number
  w: number
  h: number
}

export interface PageText {
  page: number
  runs: TextRun[]
}

/** A blank drawn with characters — `______` or a row of dots — on one page. */
export interface FillLine {
  page: number
  x0: number
  x1: number
  y: number
}

export interface Candidate {
  /** Stable while the document is open, so a dismissal survives re-reading. */
  id: string
  kind: FieldKind
  documentIndex: number
  page: number
  rect: NormRect
  /** The prompt a labelled box would carry, taken from the document's wording. */
  label: string
  /** The line as the document prints it, so the sender recognises the place. */
  source: string
  /** Text around the place, which is how it is matched to a person. */
  context: string
}

interface Line {
  y: number
  h: number
  text: string
  /** Where each run starts in `text`, so a character index maps back to x. */
  spans: { start: number; run: TextRun }[]
  fills: { x0: number; x1: number; start: number; end: number }[]
}

// Order matters: a longer phrase has to claim its words before a shorter
// pattern inside it does, or "Date signed" becomes a signature.
const LABELS: { kind: FieldKind; re: RegExp }[] = [
  { kind: 'date_signed', re: /\bdate(?:\s+signed)?\b(?!\s+of\b)/gi },
  { kind: 'initials', re: /\binitials?(?:\s+here)?\b/gi },
  { kind: 'signature', re: /\b(?:signature|signatory|sign\s+here|signed|witness(?:ed)?(?:\s+by)?(?!\s+name))\b/gi },
  { kind: 'text', re: /\b(?:(?:full|print(?:ed)?)\s+)?name\b/gi },
  { kind: 'text', re: /\b(?:job\s+)?(?:title|position)\b/gi },
]

const FILL = /_{3,}|\.{8,}|…{3,}/g
const CHECKBOX = /[☐□❏❑▢]|\[\s\]/g
const LINE_TOLERANCE = 0.004
/** Lines longer than this are prose, and a "signed" in prose is not a place to sign. */
const SHORT_LINE = 70

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

function linesOf(runs: TextRun[]): Line[] {
  const sorted = [...runs].sort((a, b) => a.y - b.y || a.x - b.x)
  const groups: TextRun[][] = []
  for (const run of sorted) {
    const last = groups[groups.length - 1]
    if (last && Math.abs(last[0].y - run.y) <= Math.max(LINE_TOLERANCE, last[0].h * 0.3)) {
      last.push(run)
    } else {
      groups.push([run])
    }
  }
  return groups.map((group) => {
    group.sort((a, b) => a.x - b.x)
    let text = ''
    const spans: Line['spans'] = []
    for (const run of group) {
      if (text) text += ' '
      spans.push({ start: text.length, run })
      text += run.str
    }
    const line: Line = { y: group[0].y, h: Math.max(...group.map((r) => r.h)), text, spans, fills: [] }
    for (const m of text.matchAll(FILL)) {
      const start = m.index ?? 0
      const end = start + m[0].length
      const fill = { x0: xAt(line, start), x1: xAt(line, end), start, end }
      const prev = line.fills[line.fills.length - 1]
      if (prev && fill.x0 - prev.x1 < 0.005) {
        prev.x1 = fill.x1
        prev.end = end
      } else if (fill.x1 - fill.x0 >= 0.03) {
        line.fills.push(fill)
      }
    }
    return line
  })
}

/** The x of a character in a line, assuming even spacing within its run. */
function xAt(line: Line, index: number): number {
  let span = line.spans[0]
  for (const s of line.spans) {
    if (s.start <= index) span = s
    else break
  }
  const { run } = span
  const per = run.str.length ? run.w / run.str.length : 0
  return run.x + Math.min(index - span.start, run.str.length) * per
}

const words = (s: string) => s.replace(FILL, ' ').replace(/\s+/g, ' ').trim()
const tidy = (s: string) => s.replace(FILL, '____').replace(/\s+/g, ' ').trim()

function classify(label: string): FieldKind | null {
  for (const { kind, re } of LABELS) {
    re.lastIndex = 0
    if (re.test(label)) return kind
  }
  return null
}

/** What a box is called when the document's own wording is the best name for it. */
function promptFrom(label: string): string {
  const t = words(label).replace(/[:*]+$/, '').trim()
  return t.length > 40 ? t.slice(0, 40).trim() : t
}

/**
 * A box sitting on a line. It is no taller than the gap to the line above, so
 * on a tightly set form it does not cover the question it answers — or the
 * box for the one before it.
 */
function boxOnLine(kind: FieldKind, x0: number, x1: number, baseline: number, ceiling: number): NormRect {
  const spec = specFor(kind).size
  const { w } = spec
  const h = clamp(baseline - ceiling - 0.006, spec.h * 0.5, spec.h)
  const width = kind === 'checkbox' ? w : clamp(x1 - x0, 0.06, Math.max(0.06, 1 - x0))
  return {
    x: clamp(x0, 0, 1 - width),
    y: clamp(baseline - h, 0, 1 - h),
    w: width,
    h,
  }
}

function overlaps(a: NormRect, b: NormRect, pad = 0): boolean {
  return a.x - pad < b.x + b.w && b.x - pad < a.x + a.w
    && a.y - pad < b.y + b.h && b.y - pad < a.y + a.h
}

/**
 * The text a box belongs with: the lines above it, where a block says who is
 * signing, and only its own caption below. Reaching further down reads the
 * next block's "Signed by …" as this one's.
 */
function contextAround(lines: Line[], rect: NormRect): string {
  return lines
    .filter((l) => l.y >= rect.y - 0.12 && l.y <= rect.y + rect.h + 0.03)
    .flatMap((l) => l.spans
      .filter(({ run }) => run.x < rect.x + rect.w + 0.3 && run.x + run.w > rect.x - 0.1)
      .map(({ run }) => run.str))
    .join(' ')
}

/** Every blank drawn with characters on these pages. They are what a dragged box sticks to. */
export function fillLinesOf(pages: PageText[]): FillLine[] {
  return pages.flatMap((p) => linesOf(p.runs).flatMap((l) => l.fills.map((f) => ({
    page: p.page, x0: f.x0, x1: f.x1, y: l.y,
  }))))
}

/**
 * The places one document asks to be signed, dated, initialled or filled in.
 *
 * Three shapes are recognised, which between them cover how contracts are
 * actually typeset: a label and its blank on one line (`Signature: ______`), a
 * blank with its caption underneath (`______` over `Authorised signatory`),
 * and a label with nothing after it but room (`Date:`). A labelled blank whose
 * label is not one of the known words becomes a text box carrying that label,
 * because "ABN: ______" is asking for exactly one thing.
 */
export function detectCandidates(
  pages: PageText[],
  documentIndex: number,
  { fieldsSupported = true }: { fieldsSupported?: boolean } = {},
): Candidate[] {
  const found: Candidate[] = []

  function add(kind: FieldKind, page: number, rect: NormRect, label: string, source: string, lines: Line[]) {
    if (!fieldsSupported && kind !== 'signature') return
    if (found.some((c) => c.documentIndex === documentIndex && c.page === page && overlaps(c.rect, rect))) return
    found.push({
      id: `${documentIndex}:${page}:${kind}:${Math.round(rect.x * 1000)}:${Math.round(rect.y * 1000)}`,
      kind,
      documentIndex,
      page,
      rect,
      label: specFor(kind).labelled ? label : '',
      source: tidy(source),
      context: contextAround(lines, rect),
    })
  }

  for (const { page, runs } of pages) {
    const lines = linesOf(runs)
    lines.forEach((line, li) => {
      const short = words(line.text).length <= SHORT_LINE
      const ceiling = li > 0 ? lines[li - 1].y : 0

      // A blank takes the label to its left, back to the previous blank.
      line.fills.forEach((fill, fi) => {
        const from = fi > 0 ? line.fills[fi - 1].end : 0
        const left = line.text.slice(from, fill.start)
        let label = words(left)
        let source = line.text
        if (!label) {
          // Nothing to its left: the caption is on the line underneath.
          const below = lines.slice(li + 1).find((l) => l.y > line.y && l.y - line.y < 0.035
            && l.spans.some(({ run }) => run.x < fill.x1 && run.x + run.w > fill.x0))
          if (!below) return
          label = words(below.spans
            .filter(({ run }) => run.x < fill.x1 && run.x + run.w > fill.x0 - 0.02)
            .map(({ run }) => run.str).join(' '))
          source = `${line.text} ${label}`
        }
        // A long run-in label is a sentence with a gap in it — "made between
        // ______ and ______" — which the sender fills in before sending.
        if (!label || label.length > 40 || !/[a-z]{2,}/i.test(label)) return
        const kind = classify(label) ?? 'text'
        add(kind, page, boxOnLine(kind, fill.x0, fill.x1, line.y, ceiling), promptFrom(label), source, lines)
      })

      // A known label with a colon and room after it, but no blank drawn.
      if (short && !line.fills.length) {
        for (const { kind, re } of LABELS) {
          re.lastIndex = 0
          for (const m of line.text.matchAll(re)) {
            const end = (m.index ?? 0) + m[0].length
            const rest = line.text.slice(end)
            if (!/^\s*:\s*$/.test(rest)) continue
            const x0 = xAt(line, line.text.length) + 0.01
            const width = specFor(kind).size.w
            if (1 - x0 < 0.08) continue
            add(kind, page, boxOnLine(kind, x0, x0 + width, line.y, ceiling), promptFrom(line.text), line.text, lines)
          }
        }
      }

      for (const m of line.text.matchAll(CHECKBOX)) {
        const at = m.index ?? 0
        const next = line.text.slice(at + m[0].length).search(CHECKBOX)
        const after = line.text.slice(at + m[0].length, next < 0 ? undefined : at + m[0].length + next)
        const x0 = xAt(line, at)
        const rect = boxOnLine('checkbox', x0, x0, line.y + line.h * 0.15, ceiling)
        add('checkbox', page, rect, promptFrom(after).slice(0, 80), after, lines)
      }
    })
  }
  return found
}

/** Who a candidate can be matched to. */
export interface Party {
  name: string
  email: string
  role: RecipientRole
  members?: { name: string; email: string }[]
}

// A shared mailbox provider says nothing about which company somebody signs for.
const PUBLIC_MAIL = new Set([
  'gmail', 'googlemail', 'outlook', 'hotmail', 'live', 'yahoo', 'icloud', 'me', 'proton', 'protonmail', 'bigpond', 'example',
])

function score(context: string, party: Party): number {
  const text = ` ${context.toLowerCase().replace(/[^a-z0-9@.]+/g, ' ')} `
  let s = 0
  const people = [{ name: party.name, email: party.email }, ...(party.members ?? [])]
  for (const p of people) {
    const name = p.name.trim().toLowerCase()
    if (name.length >= 3 && text.includes(` ${name} `)) s += 3
    else if (name.split(/\s+/).some((t) => t.length >= 3 && text.includes(` ${t} `))) s += 1
    const org = p.email.split('@')[1]?.split('.')[0]?.toLowerCase()
    if (org && org.length >= 3 && !PUBLIC_MAIL.has(org) && text.includes(` ${org}`)) s += 2
  }
  const witnessed = /\bwitness/.test(text)
  if (witnessed && party.role === 'witness') s += 2
  if (witnessed !== (party.role === 'witness')) s -= 2
  return s
}

const readingOrder = (a: Candidate, b: Candidate) =>
  a.documentIndex - b.documentIndex || a.page - b.page || a.rect.y - b.rect.y || a.rect.x - b.rect.x

/**
 * Whose each candidate is, by index into `parties`, or null when it cannot be
 * told and should go to whoever the sender is working on.
 *
 * A signature line is matched to a person by what is printed around it — their
 * name, their company from their email address, or "witness" for a witness.
 * Lines nothing identifies are dealt out in reading order to the people not yet
 * matched on that document, which is how signature blocks are laid out: first
 * party first. Each person gets at most one signature per document, because
 * that is all the seal allows. A date or a name box beside a signature line is
 * part of its block and goes with it.
 *
 * `known` is who already has a box on a candidate. Those are settled, and they
 * still anchor their block: a sender who dragged Bob's signature onto the
 * first line has said whose block it is.
 */
export function assignCandidates(
  cands: Candidate[],
  parties: Party[],
  known: readonly (number | null)[] = [],
): (number | null)[] {
  const signing = parties.flatMap((p, i) => (signsDocument(p.role) ? [i] : []))
  const out: (number | null)[] = cands.map((_, i) => known[i] ?? null)
  if (signing.length === 0) return out
  if (signing.length === 1) return cands.map((_, i) => known[i] ?? signing[0])

  const order = cands.map((c, i) => ({ c, i })).sort((a, b) => readingOrder(a.c, b.c))
  const signatures = order.filter(({ c }) => c.kind === 'signature')

  const blockOf = new Map<number, number>()
  for (const { c, i } of order) {
    if (c.kind === 'signature' || c.kind === 'initials' || c.kind === 'checkbox') continue
    const bottom = c.rect.y + c.rect.h
    const near = signatures
      .filter(({ c: s }) => s.documentIndex === c.documentIndex && s.page === c.page)
      .map(({ c: s, i: si }) => {
        const dy = Math.abs(s.rect.y + s.rect.h - bottom)
        const sameLine = dy <= 0.02
        const sameColumn = Math.abs(s.rect.x - c.rect.x) <= 0.3 && dy <= 0.15
        return { si, d: dy + Math.abs(s.rect.x - c.rect.x) * 0.1, ok: sameLine || sameColumn }
      })
      .filter((x) => x.ok)
      .sort((a, b) => a.d - b.d)[0]
    if (near) blockOf.set(i, near.si)
  }

  const best = (context: string, allowed: number[]): number | null => {
    const scored = allowed.map((pi) => ({ pi, s: score(context, parties[pi]) })).sort((a, b) => b.s - a.s)
    if (!scored.length || scored[0].s <= 0) return null
    if (scored[1] && scored[1].s === scored[0].s) return null
    return scored[0].pi
  }

  const docs = [...new Set(signatures.map(({ c }) => c.documentIndex))]
  for (const d of docs) {
    const here = signatures.filter(({ c }) => c.documentIndex === d)
    const used = new Set(here.flatMap((s) => (out[s.i] === null ? [] : [out[s.i] as number])))
    const contextOf = (s: { c: Candidate; i: number }) => [s.c.context, ...order
      .filter(({ i }) => blockOf.get(i) === s.i).map(({ c }) => c.context)].join(' ')
    for (const s of here) {
      if (out[s.i] !== null) continue
      const pi = best(contextOf(s), signing.filter((p) => !used.has(p)))
      if (pi !== null) {
        out[s.i] = pi
        used.add(pi)
      }
    }
    for (const s of here) {
      if (out[s.i] !== null) continue
      const witnessed = /\bwitness/i.test(contextOf(s))
      const free = signing.filter((p) => !used.has(p))
      const pi = free.find((p) => (parties[p].role === 'witness') === witnessed) ?? free[0]
      if (pi === undefined) continue
      out[s.i] = pi
      used.add(pi)
    }
  }

  for (const { c, i } of order) {
    if (c.kind === 'signature' || known[i] != null) continue
    const home = blockOf.get(i)
    out[i] = home !== undefined ? out[home] : best(c.context, signing)
  }
  return out
}

/**
 * Whether a box already sits where a candidate is, so it is neither suggested
 * again nor reported as missed. Only boxes the sender has put down count: an
 * unplaced one follows the page being looked at, and would otherwise cover
 * whatever happened to be under it.
 */
export function isCovered(
  c: Candidate,
  fields: readonly { documentIndex: number; page: number; rect: NormRect; placed: boolean }[],
): boolean {
  return fields.some((f) => f.placed && f.documentIndex === c.documentIndex && f.page === c.page
    && covers(f.rect, c.rect))
}

/**
 * Whether a box is on a place rather than beside it. A tall signature box
 * brushes the line under it on a tightly set form, and brushing "Print name"
 * is not answering it.
 */
function covers(box: NormRect, place: NormRect): boolean {
  const dx = Math.min(box.x + box.w, place.x + place.w) - Math.max(box.x, place.x)
  const dy = Math.min(box.y + box.h, place.y + place.h) - Math.max(box.y, place.y)
  return dx >= place.w * 0.3 && dy >= place.h * 0.4
}

/**
 * A dragged box, pulled onto a blank it is nearly on.
 *
 * The bottom edge sits on the line, because that is where handwriting sits,
 * and the left edge lines up with the start of the blank when it is close.
 * Only near a blank: pulling toward every line of text would fight the sender
 * across a whole paragraph.
 *
 * The reach is given per axis, as fractions of the page, because it should be
 * the same few pixels under a finger on a phone as under a mouse on a desktop.
 */
export function snapToLines(
  rect: NormRect,
  lines: readonly FillLine[],
  reach: { x: number; y: number } = { x: 0.02, y: 0.015 },
): NormRect {
  let bestLine: FillLine | null = null
  let bestGap = reach.y
  for (const l of lines) {
    if (rect.x >= l.x1 || rect.x + rect.w <= l.x0) continue
    const gap = Math.abs(rect.y + rect.h - l.y)
    if (gap <= bestGap) {
      bestGap = gap
      bestLine = l
    }
  }
  if (!bestLine) return rect
  const x = Math.abs(rect.x - bestLine.x0) <= reach.x ? bestLine.x0 : rect.x
  return {
    ...rect,
    x: clamp(x, 0, 1 - rect.w),
    y: clamp(bestLine.y - rect.h, 0, 1 - rect.h),
  }
}
